import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { clearToken, getToken, setToken } from '../config/api';
import { isEmbedded } from '../config/embedded';
import { fetchMe, fetchSession, login as apiLogin } from '../api/brands';

// Auth/session context for the partner portal. Mirrors the admin flow: token in
// localStorage, `GET /user/me` for the session (which carries `stores[]` — the
// user's brands).
//
// Inside the Shopify admin iframe there is no login and no token: the App Bridge
// session token is minted per request and `fetchMe()` is transparently rewritten
// to /shopify/admin/session, which returns a single-store session for the shop
// the token belongs to. See config/embedded.js.

const AuthContext = createContext({
  user: null,
  loading: true,
  brands: [],
  embedded: false,
  returnTo: null,
  portalUrl: null,
  claimUrl: null,
  linked: false,
  login: async () => {},
  logout: () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Embedded only: where to send the merchant back once this Shopify session
  // has attached the store to their Outfino account. See config/embedded.js.
  const [returnTo, setReturnTo] = useState(null);
  // Embedded only: the portal's own address, offered whether or not there is
  // anything to bounce to, so the embedded app is never a dead end.
  const [portalUrl, setPortalUrl] = useState(null);
  const [linked, setLinked] = useState(false);
  // Embedded only: step out to the API once to claim an unowned store.
  const [claimUrl, setClaimUrl] = useState(null);

  const embedded = isEmbedded();

  const loadMe = useCallback(async () => {
    // Embedded: no stored token to check — App Bridge is the credential.
    if (!embedded && !getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { user: me, returnTo: back, portalUrl: portal, claimUrl: claim, linked: isLinked } = await fetchSession();
      setUser(me);
      setReturnTo(back);
      setPortalUrl(portal);
      setLinked(isLinked);
      setClaimUrl(claim);
    } catch (e) {
      // Expired/invalid token → drop it (no refresh endpoint exists). In
      // embedded mode there is nothing to drop; a 401 means the iframe was
      // opened outside Shopify admin, and Dashboard renders that state.
      if (e.status === 401 && !embedded) clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [embedded]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(
    async (email, password) => {
      const { token, user: u } = await apiLogin(email, password);
      setToken(token);
      // Prefer a fresh /me (carries stores[]), fall back to the login user.
      try {
        const me = await fetchMe();
        setUser(me);
      } catch {
        setUser(u);
      }
    },
    []
  );

  // Used by the social (Google / Apple) flows: they already have a token +
  // user from the API, so we just persist it and hydrate /me.
  const loginWithToken = useCallback(async (token, fallbackUser) => {
    setToken(token);
    try {
      const me = await fetchMe();
      setUser(me);
    } catch {
      setUser(fallbackUser || null);
    }
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  const brands = useMemo(() => user?.stores ?? [], [user]);

  const value = useMemo(
    () => ({ user, loading, brands, embedded, returnTo, portalUrl, claimUrl, linked, login, loginWithToken, logout, refresh: loadMe }),
    [user, loading, brands, embedded, returnTo, portalUrl, claimUrl, linked, login, loginWithToken, logout, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
